import type {
  Coding,
  Extension,
  Questionnaire,
  QuestionnaireItem,
  QuestionnaireItemAnswerOption,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
} from '@medplum/fhirtypes'
import { startCase } from 'lodash-es'
import { codeableConcept } from '#src/codeableConcept.ts'
import { flattenQuestionnaireItems } from '#src/models/questionnaires/utils/flattenQuestionnaireItems.ts'
import {
  ORDINAL_VALUE_EXTENSION_URL,
  QUESTIONNAIRE_HIDDEN_EXTENSION_URL,
  QUESTIONNAIRE_ITEM_CONTROL_EXTENSION_URL,
  QUESTIONNAIRE_ITEM_CONTROL_SYSTEM,
  QUESTIONNAIRE_ITEM_TIME_RESULT_URL,
  QUESTIONNAIRE_RESPONSE_NAME_EXTENSION_URL,
} from '#src/structureDefinitions.ts'

export const VALIDATED_TOOL_TAG = {
  system: 'https://www.seenhealth.org/fhir/Questionnaire/tag',
  code: 'validated-tool',
  display: 'Validated Tool',
}

export const VALIDATED_TOOL_SUMMARY_ITEM: QuestionnaireItem = {
  linkId: 'summary',
  type: 'string',
  text: 'Add a short result summary (optional)',
}

export const getOrdinalValueExtension = (valueInteger: number): Extension => ({
  url: ORDINAL_VALUE_EXTENSION_URL,
  valueInteger,
})

export const QUESTIONNAIRE_ITEM_DROPDOWN_EXTENSION: Extension = {
  url: QUESTIONNAIRE_ITEM_CONTROL_EXTENSION_URL,
  valueCodeableConcept: codeableConcept({
    system: QUESTIONNAIRE_ITEM_CONTROL_SYSTEM,
    code: 'drop-down',
    display: 'Dropdown',
  }),
}

export const QUESTIONNAIRE_HIDDEN_EXTENSION: Extension = {
  url: QUESTIONNAIRE_HIDDEN_EXTENSION_URL,
  valueBoolean: true,
}

interface ComputedAnswerOption {
  code: string
  score: number
}

export const getComputationAnswerOptions = (
  answerOptions: ComputedAnswerOption[]
): QuestionnaireItemAnswerOption[] =>
  answerOptions.map((option) => ({
    valueCoding: {
      code: option.code,
      display: `${startCase(option.code)} (${option.score})`,
      extension: [getOrdinalValueExtension(option.score)],
    },
  }))

export const computationLinkIds = {
  RESULT: 'computation',
  SCORE: 'score',
  CODE: 'code',
  DISPLAY: 'display',
}

export const getComputationQuestionnaireItem = (
  codeAnswerOptions?: ComputedAnswerOption[]
): QuestionnaireItem => {
  const items: QuestionnaireItem[] = [
    {
      linkId: computationLinkIds.DISPLAY,
      type: 'string',
      text: 'Result',
      extension: [QUESTIONNAIRE_HIDDEN_EXTENSION],
    },
    {
      linkId: computationLinkIds.SCORE,
      type: 'integer',
      readOnly: true,
      text: 'Total Score',
      extension: [QUESTIONNAIRE_HIDDEN_EXTENSION],
    },
  ]
  if (codeAnswerOptions) {
    items.push({
      linkId: computationLinkIds.CODE,
      type: 'choice',
      readOnly: true,
      text: 'Severity',
      extension: [QUESTIONNAIRE_HIDDEN_EXTENSION],
      answerOption: getComputationAnswerOptions(codeAnswerOptions),
    })
  }
  return {
    linkId: computationLinkIds.RESULT,
    type: 'group',
    item: items,
  }
}

interface ValidatedToolComputation {
  display?: string
  score?: number
  code?: string
}

export const getValidatedToolComputation = (
  response: QuestionnaireResponse
): ValidatedToolComputation | null => {
  const computation = response.item?.find(
    (x) => x.linkId === computationLinkIds.RESULT
  )?.item
  if (!computation) {
    return null
  }
  return {
    display: getAnswerString(
      getItemByLinkId(computation, computationLinkIds.DISPLAY)
    ),
    score: getAnswerNumber(
      getItemByLinkId(computation, computationLinkIds.SCORE)
    ),
    code: getItemByLinkId(computation, computationLinkIds.CODE)?.answer?.[0]
      ?.valueCoding?.code,
  }
}

export const getValidatedToolName = (
  response: QuestionnaireResponse
): string | undefined =>
  response.extension?.find(
    (ext) => ext.url === QUESTIONNAIRE_RESPONSE_NAME_EXTENSION_URL
  )?.valueString

export const getValidatedToolSummary = (
  response: QuestionnaireResponse
): string | undefined =>
  getAnswerString(
    getItemByLinkId(response.item ?? [], VALIDATED_TOOL_SUMMARY_ITEM.linkId)
  )

interface ValidatedToolResult {
  result: QuestionnaireResponse
  isUpdated: boolean
}

export const computeValidatedToolResult = (
  questionnaire: Questionnaire,
  response: QuestionnaireResponse
): ValidatedToolResult => {
  // Get all answers that have a score (ordinal value)
  const answers = flattenQuestionnaireItems<QuestionnaireResponseItem>(response)
  const answersWithScores = answers.filter((item) =>
    item.answer?.some((a) =>
      a.valueCoding?.extension?.some(
        (e) => e.url === ORDINAL_VALUE_EXTENSION_URL
      )
    )
  )

  const itemResultAnswer = answers.filter(
    (item) => item.linkId === QUESTIONNAIRE_ITEM_TIME_RESULT_URL
  )

  // If no answers, return original response
  if (!answersWithScores.length && !itemResultAnswer.length) {
    return {
      isUpdated: false,
      result: response,
    }
  }

  let score: number

  if (itemResultAnswer.length) {
    score = itemResultAnswer[0].answer?.[0]?.valueInteger ?? 0

    const updatedResponse: QuestionnaireResponse = {
      ...response,
      item: [
        ...(response.item?.filter(
          (x) => x.linkId !== computationLinkIds.RESULT
        ) ?? []),
        {
          linkId: computationLinkIds.RESULT,
          item: [
            {
              linkId: computationLinkIds.DISPLAY,
              answer: [
                {
                  valueString: `Time in Seconds: (${score})`,
                },
              ],
            },
            {
              linkId: computationLinkIds.SCORE,
              answer: [{ valueInteger: score }],
            },
            {
              linkId: computationLinkIds.CODE,
              answer: [
                {
                  valueCoding: {
                    code: 'time-in-seconds',
                    display: 'Time in Seconds',
                    extension: [
                      {
                        url: QUESTIONNAIRE_ITEM_TIME_RESULT_URL,
                        valueInteger: score,
                      },
                    ],
                  },
                },
              ],
            },
          ],
        },
      ],
    }

    return {
      result: updatedResponse,
      isUpdated: true,
    }
  } else {
    // Compute the total score using the responses and ordinal value map
    score = answersWithScores.reduce((total, item) => {
      if (item.answer) {
        const itemScore = item.answer.reduce((answerTotal, a) => {
          if (a.valueCoding?.extension) {
            const extension = a.valueCoding.extension.find(
              (e) => e.url === ORDINAL_VALUE_EXTENSION_URL
            )
            if (extension?.valueInteger) {
              return answerTotal + extension.valueInteger
            }
          }
          return answerTotal
        }, 0)
        return total + itemScore
      }
      return total
    }, 0)
  }

  // Compute the result code based on the total score
  const questions = flattenQuestionnaireItems<QuestionnaireItem>(questionnaire)
  let resultCoding: Coding | undefined
  const resultAnswerOptions =
    questions.find((item) => item.linkId === computationLinkIds.CODE)
      ?.answerOption ?? []
  for (const { valueCoding } of resultAnswerOptions) {
    const ordinalExtension = valueCoding?.extension?.find(
      (ext) => ext.url === ORDINAL_VALUE_EXTENSION_URL
    )
    const ordinalValue = ordinalExtension
      ? ordinalExtension.valueInteger
      : undefined

    resultCoding = { code: valueCoding?.code, display: valueCoding?.display }

    // Stop if the score is less than or equal to the current answer option ordinal value
    if (ordinalValue !== undefined && score <= ordinalValue) {
      break
    }
  }

  const currentScore = answers.find(
    (x) => x.linkId === computationLinkIds.SCORE
  )?.answer?.[0]?.valueInteger
  const currentCode = answers.find((x) => x.linkId === computationLinkIds.CODE)
    ?.answer?.[0]?.valueCoding

  // If the score and result code are the same as the current response, return the original response
  if (currentScore === score && currentCode?.code === resultCoding?.code) {
    return {
      result: response,
      isUpdated: false,
    }
  }

  // Update the response with the computed score and result code
  // Score is required for computation, a result code is optional
  const updatedResponse: QuestionnaireResponse = {
    ...response,
    item: [
      ...(response.item?.filter(
        (x) => x.linkId !== computationLinkIds.RESULT
      ) ?? []),
      {
        linkId: computationLinkIds.RESULT,
        item: [
          {
            linkId: computationLinkIds.DISPLAY,
            answer: [
              {
                valueString: `${
                  startCase(resultCoding?.code) ?? 'Total Score:'
                } (${score})`,
              },
            ],
          },
          {
            linkId: computationLinkIds.SCORE,
            answer: [{ valueInteger: score }],
          },
          ...(resultCoding
            ? [
                {
                  linkId: computationLinkIds.CODE,
                  answer: [{ valueCoding: resultCoding }],
                },
              ]
            : []),
        ],
      },
    ],
  }

  return {
    result: updatedResponse,
    isUpdated: true,
  }
}

// Helpers

export function getItemByLinkId(
  items: QuestionnaireResponseItem[],
  linkId: string
): QuestionnaireResponseItem | undefined {
  return items?.find((item) => item.linkId === linkId)
}

export function selectAnswerForItem(
  item: QuestionnaireResponseItem | undefined
): QuestionnaireResponseItemAnswer | undefined {
  // Assume one answer per item
  return item?.answer?.[0]
}

export function getAnswerString(
  item: QuestionnaireResponseItem | undefined
): string | undefined {
  const answer = selectAnswerForItem(item)
  if (answer) {
    if (typeof answer.valueString === 'string') {
      return answer.valueString
    }
    if (answer.valueCoding && typeof answer.valueCoding.display === 'string') {
      return answer.valueCoding.display
    }
    if (typeof answer.valueBoolean === 'boolean') {
      return answer.valueBoolean ? 'Yes' : 'No'
    }
  }
  return undefined
}

export function getAnswerNumber(
  item: QuestionnaireResponseItem | undefined
): number | undefined {
  const answer = selectAnswerForItem(item)
  if (answer) {
    if (typeof answer.valueInteger === 'number') {
      return answer.valueInteger
    }
    if (typeof answer.valueDecimal === 'number') {
      return answer.valueDecimal
    }
  }
  return undefined
}
